import { useState, useCallback, useEffect } from 'react';
import { Button, Card, Form, OverlayTrigger, Popover } from 'react-bootstrap';
import { format } from 'date-fns';

import BransjePicker, { PickedBransje } from 'components/BransjePicker';
import GrossistPicker, { PickedGrossist } from 'components/GrossistPicker';
import GrupperingPicker from 'components/GrupperingPicker';
import { IEnhetsgruppeNode } from 'components/GrupperingPicker/types';
import KjedePicker, { PickedKjede, PickerMode } from 'components/KjedePicker';
import EnhetTypeMultiPicker from 'components/EnhetTypeMultiPicker';
import BrukerPicker, { IMappedBruker } from 'components/BrukerPicker';

import { EnhetSearchFilter } from 'services/KjederegisteretAdminApi/EnhetSearchApi/types';
import { KjedeType } from 'services/KjederegisteretAdminApi/KjedeApi/types';

import AktivFilterRadio from './AktivFilterRadio';
import OtherFilters from './OtherFilters';
import { PickedType } from 'components/EnhetTypeMultiPicker/types';
import MottakerTypeahead from 'components/MottakerTypeahead';
import { Mottaker } from 'services/KjederegisteretAdminApi/MottakerApi/types';
import { IcebergIcon } from '@tradesolution/iceberg-ui-react';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';

interface Props {
  onFilterChanged: (filter: EnhetSearchFilter) => void;
  filter: EnhetSearchFilter;
  /** Antall treff pr. bransje-id fra fasett-beregning i søket. */
  bransjeFacetCounts?: Map<number, number>;
  /** Antall treff pr. regional-kjedenr fra fasett-beregning i søket. */
  regionalKjedeFacetCounts?: Map<number, number>;
  /** Antall treff pr. markeds-kjedenr fra fasett-beregning i søket. */
  markedsKjedeFacetCounts?: Map<number, number>;
  /** Antall treff pr. enhetsgrupperingsnr fra fasett-beregning i søket. */
  enhetsgrupperingFacetCounts?: Map<number, number>;
  /** Antall treff pr. grossistnr fra fasett-beregning i søket. */
  grossistFacetCounts?: Map<number, number>;
  /** Antall treff pr. enhettype-kode fra fasett-beregning i søket. */
  enhetTypeFacetCounts?: Map<string, number>;
  /** Antall treff pr. opprettet-valg (today/yesterday/week/month) fra fasett-beregning i søket. */
  opprettetFacetCounts?: Map<string, number>;
  /** Antall enheter som mangler egenskapen pr. "Andre filtre"-checkbox-value fra fasett-beregning. */
  andreFiltreFacetCounts?: Map<string, number>;
  /** Antall treff pr. aktiv-status (Active/Closed/NotOpenedYet) fra fasett-beregning i søket. */
  aktivFacetCounts?: Map<string, number>;
  /** Antall treff pr. bruker-id (sist endret av) fra fasett-beregning i søket. */
  sistEndretAvFacetCounts?: Map<string, number>;
}

const FilterSearch = (props: Props) => {
  const [hideEmptyFacets, setHideEmptyFacets] = useState<boolean>(false);
  const [showOpprettetDateInputs, setShowOpprettetDateInputs] = useState<boolean>(
    props.filter.opprettetFilter === 'custom',
  );
  const [customFrom, setCustomFrom] = useState<Date>();
  const [customTo, setCustomTo] = useState<Date>();

  const handleOtherFiltersChanged = (value, checked) => {
    switch (value) {
      case 'IkkeGln':
        props.onFilterChanged({ ...props.filter, glnFilter: checked ? 'UtenGln' : 'All' });
        break;
      case 'IkkeHovedBransje':
        props.onFilterChanged({
          ...props.filter,
          utenHovedbransjeFilter: checked ? 'UtenHovedbransje' : 'All',
        });
        break;
      case 'IkkeKomplett':
        props.onFilterChanged({
          ...props.filter,
          komplettTidspunktFilter: checked ? 'UtenKomplettTidspunkt' : 'All',
        });
        break;
      case 'IkkePublisert':
        props.onFilterChanged({
          ...props.filter,
          ikkePublisertFilter: checked ? 'IkkePublisert' : 'All',
        });
        break;
      case 'FeilOpprettet':
        props.onFilterChanged({
          ...props.filter,
          feilOpprettetFilter: checked ? 'FeilOpprettet' : 'All',
        });
        break;
      case 'IkkeOppstartsdato':
        props.onFilterChanged({
          ...props.filter,
          oppstartsdatoFilter: checked ? 'UtenOppstartsdato' : 'All',
        });
        break;
      default:
        throw new Error('Ukjent option!');
    }
  };

  const handleActiveFilterChanged = (value: string) => {
    props.onFilterChanged({ ...props.filter, aktivFilter: value });
  };

  // workaround because the picker component does not handle beeing a controlled component great
  const [selectedRegionalKjeder, setSelectedRegionalKjeder] = useState<PickedKjede[]>([]);
  const handleRegionalkjedeSelected = useCallback((kjedeList: PickedKjede[]) => {
    setSelectedRegionalKjeder(kjedeList);
  }, []);

  useEffect(() => {
    props.onFilterChanged({
      ...props.filter,
      regionKjedeFilter: selectedRegionalKjeder.map((o: PickedKjede) => parseInt(o.kjedenr)),
    });
  }, [selectedRegionalKjeder]);

  const [selectedMarkedsKjeder, setSelectedMarkedsKjeder] = useState<PickedKjede[]>([]);
  const handleMarkedskjedeSelected = useCallback((kjedeList: PickedKjede[]) => {
    setSelectedMarkedsKjeder(kjedeList);
  }, []);

  useEffect(() => {
    props.onFilterChanged({
      ...props.filter,
      salgsOgMarkedsforingKjedeFilter: selectedMarkedsKjeder.map((o: PickedKjede) =>
        parseInt(o.kjedenr),
      ),
    });
  }, [selectedMarkedsKjeder]);

  const [selectedBransjer, setSelectedBransjer] = useState<PickedBransje[]>([]);
  const handleBransjeSelectionChanged = useCallback((bransjeList: PickedBransje[]) => {
    setSelectedBransjer(bransjeList);
  }, []);

  useEffect(() => {
    props.onFilterChanged({ ...props.filter, bransjeFilter: selectedBransjer.map((o) => o.id) });
  }, [selectedBransjer]);

  const [selectedEnhetTyper, setSelectedEnhetTyper] = useState<PickedType[]>([]);
  const onEnhetTypeSelectionChanged = (pickedTyper: PickedType[]) => {
    setSelectedEnhetTyper(pickedTyper);
  };
  useEffect(() => {
    props.onFilterChanged({
      ...props.filter,
      enhetTypeFilter: selectedEnhetTyper.map((o) => o.kode),
    });
  }, [selectedEnhetTyper]);

  const [selectedGrossists, setSelectedGrossists] = useState<PickedGrossist[]>([]);
  const handleGrossistSelectionChanged = useCallback((pickedGrossists: PickedGrossist[]) => {
    setSelectedGrossists(pickedGrossists);
  }, []);

  useEffect(() => {
    props.onFilterChanged({ ...props.filter, grossistFilter: selectedGrossists.map((o) => o.nr) });
  }, [selectedGrossists]);

  const [selectedMottakerTsKundeId, setSelectedMottakerTsKundeId] = useState<string | undefined>(
    props.filter.mottakerTsKundeId,
  );
  const handleMottakerSelectionChanged = (mottaker: Mottaker) => {
    setSelectedMottakerTsKundeId(mottaker?.tsKundeId);
  };

  useEffect(() => {
    props.onFilterChanged({ ...props.filter, mottakerTsKundeId: selectedMottakerTsKundeId });
  }, [selectedMottakerTsKundeId]);

  const [selectedBrukere, setSelectedBrukere] = useState<IMappedBruker[]>([]);
  const handleSistEndretAvChanged = useCallback((pickedBrukere: IMappedBruker[]) => {
    setSelectedBrukere(pickedBrukere);
  }, []);

  useEffect(() => {
    props.onFilterChanged({
      ...props.filter,
      sistEndretAv: selectedBrukere.map((x) => x.brukerId),
    });
  }, [selectedBrukere]);

  const handleEnhetsgrupperingSelectionChanged = useCallback(
    (selectedGrupperinger: IEnhetsgruppeNode[]) => {
      props.onFilterChanged({
        ...props.filter,
        enhetsgrupperingFilter: selectedGrupperinger.map((x) => x.nr),
      });
    },
    [],
  );

  // Legger til antall treff i parentes bak label når fasett-tall finnes for valget.
  // Ren streng-variant, brukes der label må være tekst (f.eks. <option>).
  const withCount = (label: string, key: string, counts?: Map<string, number>) => {
    const count = counts?.get(key);
    return count != null ? `${label} (${count.toLocaleString('nb-NO')})` : label;
  };

  // Som withCount, men returnerer JSX der antallet er mindre og mindre prominent.
  // Brukes i checkbox-/radio-labels (Form.Check) som godtar ReactNode.
  const withCountNode = (label: string, key: string, counts?: Map<string, number>) => {
    const count = counts?.get(key);
    if (count == null) return label;
    return (
      <>
        {label}{' '}
        <small className="text-muted" style={{ fontSize: '0.75em' }}>
          ({count.toLocaleString('nb-NO')})
        </small>
      </>
    );
  };

  // Aktiv-fasetten har tre statuser; "Alle" utledes som summen av dem.
  const aktivCountsMedAlle = (() => {
    if (!props.aktivFacetCounts || props.aktivFacetCounts.size === 0) return undefined;
    const map = new Map(props.aktivFacetCounts);
    let sum = 0;
    map.forEach((v) => (sum += v));
    map.set('All', sum);
    return map;
  })();

  const onCreatedChanged = (value: string) => {
    if (value === 'custom') {
      setShowOpprettetDateInputs(true);
      props.onFilterChanged({ ...props.filter, opprettetFilter: value });
    } else {
      setShowOpprettetDateInputs(false);
      props.onFilterChanged({ ...props.filter, opprettetFilter: value });
    }
  };

  const handleCustomFilterClicked = () => {
    const value = format(customFrom, 'dd.MM.yyyy') + '-' + format(customTo, 'dd.MM.yyyy');
    props.onFilterChanged({ ...props.filter, customOpprettetFilter: value });
  };

  const findPreselectedNumericValueInFilter = (picker: string) => {
    if (picker === 'gruppering') {
      return props.filter.enhetsgrupperingFilter;
    }

    if (picker === 'grossister') {
      return props.filter.grossistFilter;
    }
  };

  const findPreselectedStringValueInFilter = useCallback((picker: string) => {
    if (picker === 'bruker') {
      return props.filter.sistEndretAv;
    }
  }, []);

  const mottakerInfo = (
    <Popover id="popover-basic">
      <Popover.Header as="h3">Mottakerfilter</Popover.Header>
      <Popover.Body>
        Begrenser søkeresultatet ut i fra abonnement-oppsett for mottaker. Returnerer kun publiserte
        enheter for bedre å speile resultat i public api
      </Popover.Body>
    </Popover>
  );

  return (
    <div style={{ maxWidth: '100%' }}>
      <Form.Check
        type="checkbox"
        id="hideEmptyFacets"
        className="mb-3"
        label="Skjul filtre som ikke gir treff"
        checked={hideEmptyFacets}
        onChange={(e) => setHideEmptyFacets(e.target.checked)}
      />
      <AktivFilterRadio
        title="Aktiv / Nedlagt"
        options={[
          { value: 'All', label: withCountNode('Alle', 'All', aktivCountsMedAlle) },
          { value: 'Active', label: withCountNode('Aktive', 'Active', aktivCountsMedAlle) },
          {
            value: 'Closed',
            label: withCountNode('Nedlagte', 'Closed', aktivCountsMedAlle),
          },
          {
            value: 'NotOpenedYet',
            label: withCountNode('Fremtidig oppstart', 'NotOpenedYet', aktivCountsMedAlle),
          },
        ]}
        onOptionChanged={handleActiveFilterChanged}
        value={props.filter.aktivFilter}
      />

      <Card className="mb-3">
        <Card.Header>Sist endret av</Card.Header>
        <Card.Body data-private>
          <BrukerPicker
            treeSelectMode="multiSelect"
            onSelectionChanged={handleSistEndretAvChanged}
            findPreselectedInFilter={findPreselectedStringValueInFilter}
            facetCounts={props.sistEndretAvFacetCounts}
            hideEmptyFacets={hideEmptyFacets}
          />
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Opprettet</Card.Header>
        <Card.Body data-private>
          <Form.Control
            as="select"
            value={props.filter.opprettetFilter}
            onChange={(e) => onCreatedChanged(e.target.value)}
          >
            <option value="">Velg...</option>
            <option value="today">{withCount('I dag', 'today', props.opprettetFacetCounts)}</option>
            <option value="yesterday">
              {withCount('I går', 'yesterday', props.opprettetFacetCounts)}
            </option>
            <option value="week">
              {withCount('Siste 7 dager', 'week', props.opprettetFacetCounts)}
            </option>
            <option value="month">
              {withCount('Sist måned', 'month', props.opprettetFacetCounts)}
            </option>
            <option value="custom">Custom</option>
          </Form.Control>
          {showOpprettetDateInputs && (
            <div className="mt-3">
              <Form.Group>
                <Form.Label>Fra</Form.Label> <small style={{ color: 'red' }}>*</small>
                <NorwegianDatepicker value={customFrom} onChange={(val) => setCustomFrom(val)} />
              </Form.Group>
              <Form.Group>
                <Form.Label>Til</Form.Label> <small style={{ color: 'red' }}>*</small>
                <NorwegianDatepicker value={customTo} onChange={(val) => setCustomTo(val)} />
              </Form.Group>
              <Form.Group className="mt-2">
                <Button
                  size="sm"
                  style={{ float: 'right' }}
                  variant="outline-primary"
                  onClick={handleCustomFilterClicked}
                >
                  Filtrer
                </Button>
              </Form.Group>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Enhettype</Card.Header>
        <Card.Body>
          <EnhetTypeMultiPicker
            onSelectionChanged={onEnhetTypeSelectionChanged}
            selectedNodes={props.filter.enhetTypeFilter}
            treeSelectMode="multiSelect"
            facetCounts={props.enhetTypeFacetCounts}
            hideEmptyFacets={hideEmptyFacets}
          />
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Bransje</Card.Header>
        <Card.Body>
          <BransjePicker
            onSelectionChanged={handleBransjeSelectionChanged}
            selected={props.filter.bransjeFilter}
            disabled={false}
            canSelectNedlagt={true}
            facetCounts={props.bransjeFacetCounts}
            hideEmptyFacets={hideEmptyFacets}
          />
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Regionalkjeder</Card.Header>
        <Card.Body>
          <KjedePicker
            kjedeType={KjedeType.Region}
            pickerMode={PickerMode.Multi}
            onSelectionChanged={handleRegionalkjedeSelected}
            selected={props.filter.regionKjedeFilter}
            facetCounts={props.regionalKjedeFacetCounts}
            hideEmptyFacets={hideEmptyFacets}
          />
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Markedskjeder</Card.Header>
        <Card.Body>
          <KjedePicker
            kjedeType={KjedeType.Marked}
            pickerMode={PickerMode.Multi}
            onSelectionChanged={handleMarkedskjedeSelected}
            selected={props.filter.salgsOgMarkedsforingKjedeFilter}
            facetCounts={props.markedsKjedeFacetCounts}
            hideEmptyFacets={hideEmptyFacets}
          />
          <div>
            <Form.Check
              style={{ display: 'inline-block', marginRight: '0.5rem' }}
              checked={props.filter.ekskluderMarkedskjeder}
              onChange={(e) =>
                props.onFilterChanged({ ...props.filter, ekskluderMarkedskjeder: e.target.checked })
              }
            />
            <span>Ekskluder valgte</span>
          </div>
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Enhetsgruppering</Card.Header>
        <Card.Body>
          <GrupperingPicker
            treeSelectMode="multiSelect"
            onSelectionChanged={handleEnhetsgrupperingSelectionChanged}
            findPreselectedNodesByNumber={findPreselectedNumericValueInFilter}
            customTexts={{ placeholder: 'Enhetgruppering', noMatches: 'Ingen treff' }}
            facetCounts={props.enhetsgrupperingFacetCounts}
            hideEmptyFacets={hideEmptyFacets}
          />
        </Card.Body>
      </Card>

      <Card className="mb-3">
        <Card.Header>Grossister</Card.Header>
        <Card.Body>
          <GrossistPicker
            onSelectionChanged={handleGrossistSelectionChanged}
            findPreselectedNodesByNR={findPreselectedNumericValueInFilter}
            facetCounts={props.grossistFacetCounts}
            hideEmptyFacets={hideEmptyFacets}
          />
        </Card.Body>
      </Card>
      <Card className="mb-3">
        <Card.Header>
          Mottaker
          <OverlayTrigger trigger="hover" placement="right" overlay={mottakerInfo}>
            <Button variant="" style={{ padding: '0 0 0 3px' }}>
              <IcebergIcon icon="info" />
            </Button>
          </OverlayTrigger>
        </Card.Header>
        <Card.Body>
          <MottakerTypeahead
            onHandleChange={handleMottakerSelectionChanged}
            selectedMottakerTsKundeId={selectedMottakerTsKundeId}
          />
        </Card.Body>
      </Card>
      <OtherFilters
        title="Andre filtre"
        options={[
          {
            value: 'IkkeGln',
            label: withCountNode('Ikke GLN', 'IkkeGln', props.andreFiltreFacetCounts),
            checked: props.filter.glnFilter !== 'All',
          },
          {
            value: 'IkkeHovedBransje',
            label: withCountNode(
              'Ikke hovedbransje',
              'IkkeHovedBransje',
              props.andreFiltreFacetCounts,
            ),
            checked: props.filter.utenHovedbransjeFilter !== 'All',
          },
          {
            value: 'IkkeKomplett',
            label: withCountNode('Ikke komplett', 'IkkeKomplett', props.andreFiltreFacetCounts),
            checked: props.filter.komplettTidspunktFilter !== 'All',
          },
          {
            value: 'IkkePublisert',
            label: withCountNode('Ikke publisert', 'IkkePublisert', props.andreFiltreFacetCounts),
            checked: props.filter.ikkePublisertFilter !== 'All',
          },
          {
            value: 'IkkeOppstartsdato',
            label: withCountNode(
              'Ikke oppstartsdato',
              'IkkeOppstartsdato',
              props.andreFiltreFacetCounts,
            ),
            checked: props.filter.oppstartsdatoFilter !== 'All',
          },
          {
            value: 'FeilOpprettet',
            label: withCountNode('Feil opprettet', 'FeilOpprettet', props.andreFiltreFacetCounts),
            checked: props.filter.feilOpprettetFilter !== 'All',
          },
        ]}
        onOptionChanged={handleOtherFiltersChanged}
      />
    </div>
  );
};

export default FilterSearch;
